import React, { useState } from 'react';
import { Card, Button, Collapse, Alert } from 'react-bootstrap';
import videoDownloadService from '../services/videoDownloadService';

const DashboardVideoCard = ({ 
  video,
  onDownload,
  onShare
}) => {
  const [showMatchupsPanel, setShowMatchupsPanel] = useState(false);
  const [downloadState, setDownloadState] = useState({
    isDownloading: false,
    progress: '',
    error: null
  });

  const handleDownload = async () => {
    // Don't allow download for in-progress videos
    if (video.completed === false) return;
    
    if (downloadState.isDownloading) return;

    setDownloadState({
      isDownloading: true,
      progress: 'Starting download...',
      error: null
    });

    try {
      const success = await videoDownloadService.downloadVideo(
        video.id,
        (progress) => setDownloadState(prev => ({ ...prev, progress })),
        (error) => setDownloadState(prev => ({ ...prev, error }))
      );

      if (success) {
        setDownloadState({
          isDownloading: false,
          progress: 'Download started!',
          error: null
        });
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setDownloadState(prev => ({ ...prev, progress: '' }));
        }, 3000);
      } else {
        setDownloadState({
          isDownloading: false,
          progress: '',
          error: 'Download failed. Please try again.'
        });
      }
    } catch (error) {
      setDownloadState({
        isDownloading: false,
        progress: '',
        error: error.message || 'Download failed. Please try again.'
      });
    }
  };

  return (
    <Card className="video-card glass-card h-100">
      {/* Video Thumbnail - Fully Clickable */}
      <div 
        className="video-placeholder-container position-relative" 
        style={{ 
          aspectRatio: '9/16',
          minHeight: '300px',
          maxHeight: '400px',
          overflow: 'hidden',
          borderRadius: '12px 12px 0 0',
          cursor: 'pointer'
        }}
        onClick={handleDownload}
      >
        {video.thumbnail_url ? (
          <>
            <img 
              src={video.thumbnail_url} 
              alt={video.title}
              className="w-100 h-100"
              style={{ 
                objectFit: 'cover',
                objectPosition: 'center top'
              }}
              onError={(e) => {
                // Fallback to download icon if image fails to load
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'flex';
              }}
            />
            {/* Download Icon Overlay */}
            <div 
              className="position-absolute top-50 start-50 translate-middle d-flex align-items-center justify-content-center"
              style={{
                width: '60px',
                height: '60px',
                backgroundColor: downloadState.isDownloading ? 'rgba(59, 130, 246, 0.9)' : 'rgba(0, 0, 0, 0.7)',
                borderRadius: '50%',
                pointerEvents: 'none'
              }}
            >
              {downloadState.isDownloading ? (
                <div 
                  className="spinner-border text-light" 
                  role="status"
                  style={{ 
                    width: '1.5rem', 
                    height: '1.5rem',
                    borderWidth: '0.15em'
                  }}
                >
                  <span className="visually-hidden">Loading...</span>
                </div>
              ) : (
                <i className="bi bi-download text-white" style={{ fontSize: '1.5rem' }}></i>
              )}
            </div>
          </>
        ) : (
          <div 
            className="d-flex align-items-center justify-content-center h-100"
            style={{ 
              backgroundColor: 'rgba(30, 41, 59, 0.8)'
            }}
          >
            {video.completed === false ? (
              <div className="text-center text-white">
                <i className="bi bi-hourglass-split text-warning" style={{ fontSize: '2rem' }}></i>
                <div className="mt-2" style={{ fontSize: '0.9rem' }}>
                  <div>Video in progress</div>
                  <div className="text-muted" style={{ fontSize: '0.8rem' }}>Creating your recap...</div>
                </div>
              </div>
            ) : downloadState.isDownloading ? (
              <div 
                className="spinner-border text-primary" 
                role="status"
                style={{ 
                  width: '2rem', 
                  height: '2rem',
                  borderWidth: '0.15em'
                }}
              >
                <span className="visually-hidden">Loading...</span>
              </div>
            ) : (
              <i className="bi bi-download text-primary" style={{ fontSize: '2rem' }}></i>
            )}
          </div>
        )}
      </div>

      <Card.Body className="p-3 pb-0">
        {/* Download Status */}
        {downloadState.progress && (
          <Alert variant="info" className="py-2 mb-2" style={{ fontSize: '0.875rem' }}>
            <i className="bi bi-info-circle me-2"></i>
            {downloadState.progress}
          </Alert>
        )}
        
        {downloadState.error && (
          <Alert variant="danger" className="py-2 mb-2" style={{ fontSize: '0.875rem' }}>
            <i className="bi bi-exclamation-triangle me-2"></i>
            {downloadState.error}
          </Alert>
        )}

        {/* Video Title */}
        <h5 className="video-title mb-2">
          {video.completed === false ? "We're working on creating your video" : video.title}
        </h5>
        
        {/* League, Duration, and Resolution Badges */}
        <div className="d-flex gap-2 mb-3">
          {video.completed === false ? (
            <>
              <span className="badge bg-warning">
                <i className="bi bi-hourglass-split me-1"></i>
                In Progress
              </span>
              <span className="badge bg-info">
                <i className="bi bi-clock me-1"></i>
                ~10-15 minutes
              </span>
            </>
          ) : (
            <>
              <span className="badge bg-primary">
                <i className="bi bi-people me-1"></i>
                {video.leagueName}
              </span>
              <span className="badge bg-secondary">
                <i className="bi bi-clock me-1"></i>
                {video.duration}
              </span>
              <span className="badge" style={{ backgroundColor: '#475569', color: '#ffffff' }}>
                <i className="bi bi-aspect-ratio me-1"></i>
                {video.resolution || 'Unknown'}
              </span>
            </>
          )}
        </div>

        {/* Matchups Panel */}
        {video.matchups && (
          <div className="matchups-section mb-2">
            <Button
              variant="link"
              className="p-0 text-decoration-none"
              onClick={() => setShowMatchupsPanel(!showMatchupsPanel)}
            >
              <i className="bi bi-list-ul me-2 text-primary"></i>
              <span className="text-primary">View Matchups ({video.matchups.length})</span>
              <i className={`bi bi-chevron-${showMatchupsPanel ? 'up' : 'down'} ms-2`}></i>
            </Button>
            
                          <Collapse in={showMatchupsPanel}>
                <div className="matchups-panel mt-2">
                  {video.matchups.map((matchup, index) => (
                    <div key={index} className="matchup-item mb-3 p-3 rounded position-relative" style={{ 
                      backgroundColor: 'rgba(30, 41, 59, 0.3)',
                      border: '1px solid rgba(59, 130, 246, 0.2)'
                    }}>
                      {/* Teams stacked vertically */}
                      <div className="w-100">                          
                        <div className="row">
                          <div className="col-8">
                            <div className="team-name fw-bold text-white" style={{ fontSize: '0.9rem', flex: 1, marginRight: '10px' }}>
                              {matchup.team1}
                            </div>
                          </div>
                          <div className="col-4">
                            <div className="score text-primary fw-bold text-end" style={{ fontSize: '1.1rem', minWidth: '60px', flexShrink: 0 }}>
                              {matchup.score1}
                            </div>
                          </div>
                        </div>
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="team-name fw-bold text-white" style={{ fontSize: '0.9rem', flex: 1, marginRight: '10px' }}>
                            {matchup.team2}
                          </div>
                          <div className="score text-primary fw-bold text-end" style={{ fontSize: '1.1rem', minWidth: '60px', flexShrink: 0 }}>
                            {matchup.score2}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </Collapse>
          </div>
        )}

        {/* Social Media Reminder - At Bottom */}
        {video.completed !== false && (
          <div className="social-reminder text-center mt-1">
            <a href="#social-section" className="text-decoration-none">
              <i className="bi bi-share me-2 text-primary"></i>
              <span className="text-primary" style={{ fontSize: '0.75rem' }}>Tag us if you post</span>
            </a>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default DashboardVideoCard;
